import { defineStore } from 'pinia'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { LabManagementUser } from '@/globals/typescript/models/LabManagementUser'
import {
  canLabManagementUser,
  type TLabManagementPermission,
  type TLabManagementRole,
  type TLabManagementWorkArea,
} from '@team-milva/lab-models/user'
import { i18n } from '@/locales'
import {
  LAB_ADMIN_LANGUAGES,
  LAB_MANAGEMENT_LANGUAGES,
  type TLabManagementLocale,
} from '@team-milva/lab-models/language'
import * as Sentry from '@sentry/vue'

// Impersonation ("view as") — only meaningful for a lab-admin session.
// 'self'  — the admin freely customises their own role/work-area preview,
//           no real employee involved (no audit-logging counterpart).
// 'user'  — the admin views the app as a real employee of the currently
//           selected lab; their real, currently-known mainRole/workAreas
//           are snapshotted here so canUser() can evaluate against them
//           without a live employee-list lookup on every check.
export type TImpersonationMode = 'none' | 'self' | 'user'

export interface IImpersonationSelf {
  mainRole: TLabManagementRole
  workAreas: TLabManagementWorkArea[]
}

export interface IImpersonationUser {
  userID: string
  // Snapshotted at the moment impersonation was applied — the lab the
  // impersonated employee belongs to. Switching labs always clears
  // impersonation (see accountStore.getLabAccountFromSlug), so this never
  // goes stale relative to the currently viewed lab.
  labAccountID: string
  mainRole: TLabManagementRole
  workAreas: TLabManagementWorkArea[]
}

// Persisted to sessionStorage (not localStorage) so an active impersonation
// survives a reload but never outlives the browser tab/session — nobody
// should stay "viewing as" someone else across days or on a shared machine.
const IMPERSONATION_STORAGE_KEY = 'impersonation:state'

interface IImpersonationStorageState {
  mode: TImpersonationMode
  self: IImpersonationSelf | null
  user: IImpersonationUser | null
}

function readImpersonationStorage(): IImpersonationStorageState {
  const fallback: IImpersonationStorageState = { mode: 'none', self: null, user: null }
  const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY)
  if (!raw) {
    return fallback
  }

  try {
    return { ...fallback, ...JSON.parse(raw) }
  } catch {
    return fallback
  }
}

function writeImpersonationStorage(state: IImpersonationStorageState): void {
  if (state.mode === 'none') {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    return
  }

  sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(state))
}

const persistedImpersonation = readImpersonationStorage()

export const useUserStore = defineStore('user', {
  state: () => ({
    currentUser: null as LabManagementUser | null,
    isLabAdminUser: false as boolean,
    // True once the signed-in account has been looked up in both user collections and found in
    // neither. Without it the app could not tell "not loaded yet" from "will never load" and the
    // router guard waited on `currentUser` forever (MIL-483).
    isCurrentUserMissing: false as boolean,
    showDevActions: localStorage.getItem('dev:showDevActions') === 'true',
    menuCollapsed: false as boolean,
    tabsBar: localStorage.getItem('dev:tabsBar') === 'true',

    unsubscribeCurrentUser: null as (() => void) | null,

    impersonationMode: persistedImpersonation.mode,
    impersonationSelf: persistedImpersonation.self,
    impersonationUser: persistedImpersonation.user,
  }),

  actions: {
    getCurrentUser(userID: string): Promise<LabManagementUser | null> {
      const stored = localStorage.getItem(`menu:collapsed:${userID}`)
      if (stored !== null) {
        this.menuCollapsed = stored === 'true'
      }

      if (this.currentUserLoaded) {
        return Promise.resolve(this.currentUser)
      }

      return new Promise((resolve) => {
        let resolved = false
        let unsub1: (() => void) | null = null
        let unsub2: (() => void) | null = null
        this.isCurrentUserMissing = false

        // Each collection reports whether the document exists. Only when both have said "no" is the
        // account known to have no profile; either one finding it settles the lookup.
        //
        // "No" only counts from a *server* snapshot: a cache-only one (an offline start, or nothing
        // cached yet) reports an absent document just as well, and signing out an account whose profile
        // does exist would be wrong. The listeners ask for metadata changes so that a cache-only
        // "missing" is followed by the server's real answer. Tracked per collection, since the same
        // collection can report more than once.
        const profileMissingIn = { management: false, admin: false }
        const onProfileMissing = (
          collection: 'management' | 'admin',
          docSnap: { metadata: { fromCache: boolean } }
        ) => {
          if (docSnap.metadata.fromCache) return
          profileMissingIn[collection] = true
          if (!(profileMissingIn.management && profileMissingIn.admin) || resolved) return
          if (unsub1) unsub1()
          if (unsub2) unsub2()
          this.isCurrentUserMissing = true
          resolved = true
          resolve(null)
        }

        const finish = (
          user: LabManagementUser | null,
          unsub: (() => void) | null,
          isLabAdminUser?: boolean
        ) => {
          this.currentUser = user
          this.isLabAdminUser = isLabAdminUser ?? false
          this.unsubscribeCurrentUser = unsub
          if (unsub1 && unsub1 !== unsub) unsub1()
          if (unsub2 && unsub2 !== unsub) unsub2()

          if (!resolved) {
            resolved = true
            resolve(user)
          }
        }

        unsub1 = onSnapshot(
          doc(db, 'Apps', '-lab-management-', 'Users', userID),
          { includeMetadataChanges: true },
          (docSnap) => {
            if (docSnap.exists()) {
              const currentUser = new LabManagementUser(
                docSnap.data() as LabManagementUser
              )
              // Apply the user's own persisted language on every load — the
              // only way a saved preference survives a fresh device/session,
              // since main.ts's boot sequence only ever consults localStorage
              // + browser detection before the user is known.
              const locale =
                LAB_MANAGEMENT_LANGUAGES[currentUser.languageCode]?.locale
              if (locale) {
                i18n.global.locale.value = locale as TLabManagementLocale
                localStorage.setItem('lang', locale)
              }
              finish(currentUser, unsub1)
              return
            }
            onProfileMissing('management', docSnap)
          }
        )

        unsub2 = onSnapshot(
          doc(db, 'Apps', '-lab-admin-', 'Users', userID),
          { includeMetadataChanges: true },
          (docSnap) => {
            if (docSnap.exists()) {
              const currentUser = new LabManagementUser({
                ...(docSnap.data() as LabManagementUser),
                mainRole: 'owner',
              })
              // Admins keep their own language regardless of the visited lab's
              // labLanguages — no fallback, no reset.
              const locale = LAB_ADMIN_LANGUAGES[currentUser.languageCode]?.locale
              if (locale) {
                i18n.global.locale.value = locale as TLabManagementLocale
              }
              finish(currentUser, unsub2, true)
              return
            }
            onProfileMissing('admin', docSnap)
          }
        )
      })
    },
    resetStore(): void {
      this.currentUser = null
      this.isCurrentUserMissing = false
      this.setImpersonationNone()

      if (this.unsubscribeCurrentUser) {
        this.unsubscribeCurrentUser()
        this.unsubscribeCurrentUser = null
      }
    },

    updateUserDoc(userID: string, values: Partial<LabManagementUser>): void {
      const userRef = doc(db, 'Apps', '-lab-management-', 'Users', userID)
      updateDoc(userRef, { ...values }).catch((error) => {
        Sentry.captureException(error)
      })
    },

    async updateUserDocPromise(
      userID: string,
      values: Partial<LabManagementUser>
    ): Promise<void> {
      const userRef = doc(db, 'Apps', '-lab-management-', 'Users', userID)
      try {
        await updateDoc(userRef, { ...values })
      } catch (error) {
        Sentry.captureException(error)
        throw error
      }
    },

    updateLastSeen(): void {
      if (!this.currentUser) {
        return
      }

      // Only update if more than x minutes have passed since last update
      const now = Date.now()
      const interval = 5 * 60 * 1000 // 5 minutes in milliseconds
      const lastSeen = this.currentUser.timestamps.lastSeenAt

      if (now - lastSeen < interval) {
        return
      }

      this.updateUserDoc(this.currentUser.id, {
        timestamps: {
          ...this.currentUser.timestamps, // TODO: Is required to avoid overwriting other timestamp fields
          lastSeenAt: now,
        },
      })
    },
    // Single choke point for permission evaluation against the current session.
    // Every UI conditional/router guard must call this (not
    // `currentUser.canUser()` directly) so an active impersonation override
    // is always respected.
    canUser(permission: TLabManagementPermission): boolean {
      // A lab-admin browsing without an active impersonation override gets
      // full access — there is no real employee role/work-area set to check
      // against. Picking "Mig selv" or a specific employee in the
      // impersonation modal overrides this with a real permission set below.
      if (this.isLabAdminUser && this.impersonationMode === 'none') {
        return true
      }

      if (this.impersonationMode === 'self' && this.impersonationSelf) {
        return canLabManagementUser(
          this.impersonationSelf.mainRole,
          this.impersonationSelf.workAreas,
          permission
        )
      }

      if (this.impersonationMode === 'user' && this.impersonationUser) {
        return canLabManagementUser(
          this.impersonationUser.mainRole,
          this.impersonationUser.workAreas,
          permission
        )
      }

      return this.currentUser?.canUser(permission) ?? false
    },

    // Impersonation ("view as") — see the type comments above. Every
    // mutator persists to sessionStorage so the mode survives a reload.
    setImpersonationNone(): void {
      this.impersonationMode = 'none'
      this.impersonationSelf = null
      this.impersonationUser = null
      writeImpersonationStorage({ mode: 'none', self: null, user: null })
    },

    setImpersonationSelf(
      mainRole: TLabManagementRole,
      workAreas: TLabManagementWorkArea[]
    ): void {
      this.impersonationMode = 'self'
      this.impersonationSelf = { mainRole, workAreas }
      this.impersonationUser = null
      writeImpersonationStorage({
        mode: 'self',
        self: this.impersonationSelf,
        user: null,
      })
    },

    setImpersonationUser(params: IImpersonationUser): void {
      this.impersonationMode = 'user'
      this.impersonationUser = { ...params }
      this.impersonationSelf = null
      writeImpersonationStorage({
        mode: 'user',
        self: null,
        user: this.impersonationUser,
      })
    },

    toggleShowDevActions(): void {
      this.showDevActions = !this.showDevActions
      localStorage.setItem('dev:showDevActions', String(this.showDevActions))
    },

    toggleMenuCollapsed(): void {
      this.menuCollapsed = !this.menuCollapsed
      const userID = this.currentUser?.id
      if (userID) {
        localStorage.setItem(`menu:collapsed:${userID}`, String(this.menuCollapsed))
      }
    },

  },

  getters: {
    // Loaders
    currentUserLoaded: (state) => {
      return !!state.unsubscribeCurrentUser
    },
    isDeveloper: (state) => {
      return state.currentUser?.options.isDeveloper ?? false
    },

    // Drives the impersonation icon button's "active" visual state.
    isImpersonating: (state) => {
      return state.impersonationMode !== 'none'
    },

    // Merged into every Lab Cloud API client's request headers. Only Mode
    // User carries a real, loggable "on behalf of" person — Mode Self is a
    // preview of the admin's own session, so no headers are sent for it.
    impersonationApiHeaders: (state): Record<string, string> => {
      if (state.impersonationMode === 'user' && state.impersonationUser) {
        return {
          'X-Impersonate-User-ID': state.impersonationUser.userID,
          'X-Impersonate-Lab-Account-ID': state.impersonationUser.labAccountID,
        }
      }

      return {}
    },
  },
})

